/**
 * EditBannerModal — Choose Image → Preview → Save, plus Remove (which
 * restores the default ScribbleReads banner, since "no image" and
 * "default" are the same state here). Desktop Dialog / mobile Drawer,
 * matching EditProfileModal's responsive pattern.
 */

import React, { useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { resizeImageFile } from './utils/imageUtils';
import ProfileBanner from './ProfileBanner';
import { PROFILE } from '@/constants/testIds';

function ModalBody({ currentBanner, tone, onSave, onRemove, onClose }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const handlePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setSelectedFile(file);
    setIsProcessing(true);
    try {
      const dataUrl = await resizeImageFile(file);
      setPreview(dataUrl);
    } catch (err) {
      setError(err.message || 'Could not use that image.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!preview || !selectedFile) return;
    try {
      await onSave(selectedFile);
      toast('Banner updated');
      onClose();
    } catch (err) {
      setError(err.message || 'Could not upload the banner.');
    }
  };

  const handleRemove = () => {
    onRemove();
    toast('Banner removed — showing the default ScribbleReads banner');
    onClose();
  };

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">Edit Banner</h2>
      <p className="mt-1 text-sm text-subtle">This sits behind your avatar at the top of your profile.</p>

      <div className="mt-4 overflow-hidden rounded-2xl border border-line">
        <ProfileBanner tone={tone} bannerImage={preview ?? currentBanner} isOwnProfile={false} />
      </div>

      {error && <p className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="sr-only"
        aria-label="Choose banner image"
      />

      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button variant="secondary" icon={false} onClick={handlePick} disabled={isProcessing} className="gap-1.5">
          <ImagePlus className="h-4 w-4" /> {isProcessing ? 'Processing…' : preview ? 'Choose a Different Image' : 'Choose Image'}
        </Button>
        {(currentBanner || preview) && (
          <Button variant="secondary" icon={false} onClick={handleRemove} className={cx('gap-1.5', 'text-coral')}>
            <Trash2 className="h-4 w-4" /> Remove Banner
          </Button>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="secondary" icon={false} onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon={false} onClick={handleSave} disabled={!preview}>Save Banner</Button>
      </div>
    </div>
  );
}

export default function EditBannerModal({ isOpen, onClose, currentBanner, tone, onSave, onRemove }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent data-testid={PROFILE.bannerModal} className="max-h-[88vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Edit Banner</DialogTitle>
          <ModalBody currentBanner={currentBanner} tone={tone} onSave={onSave} onRemove={onRemove} onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent data-testid={PROFILE.bannerModal} className="max-h-[92vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Edit Banner</DrawerTitle>
        <ModalBody currentBanner={currentBanner} tone={tone} onSave={onSave} onRemove={onRemove} onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
